import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LIMITS } from '@bunker/shared';
import { toast } from '@/lib/toast';
import { Modal, TextField, Label, Input } from '@heroui/react';
import { Button } from '@/components/Button';
import { Field } from '@/components/ui/Field';
import { CloseIcon, PlusIcon } from '@/components/icons';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';

/**
 * Task-list creator — opened from the composer's "Список задач" action.
 * Mirrors the poll creator: title + 1..30 items + two collaboration toggles
 * (Совместное выполнение / Совместное добавление). POSTs to
 * /api/channels/:id/tasks; the message arrives via the socket pipeline.
 */
export function TaskCreatorModal(props: {
  open: boolean;
  channelId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [items, setItems] = useState<string[]>(['']);
  const [allowComplete, setAllowComplete] = useState(true);
  const [allowAdd, setAllowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!props.open) return;
    setTitle('');
    setItems(['']);
    setAllowComplete(true);
    setAllowAdd(false);
    setSubmitting(false);
  }, [props.open]);

  function updateItem(idx: number, value: string) {
    setItems((prev) => {
      const next = prev.slice();
      next[idx] = value;
      return next;
    });
  }
  function addRow() {
    setItems((prev) => (prev.length >= 30 ? prev : [...prev, '']));
  }
  function removeRow(idx: number) {
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  }

  const validItems = items.map((o) => o.trim()).filter((o) => o.length > 0);
  const canSubmit = title.trim().length > 0 && validItems.length >= 1 && !submitting;

  async function submit() {
    if (!props.channelId || !canSubmit) return;
    setSubmitting(true);
    try {
      await api.createTaskList(props.channelId, {
        title: title.trim(),
        items: validItems,
        allowComplete,
        allowAdd,
      });
      toast.success(t('cmod.task.published'));
      props.onClose();
    } catch (err) {
      toast.error(...errToToast(err, t('cmod.task.createError')));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal.Backdrop isOpen={props.open} onOpenChange={(o) => !o && !submitting && props.onClose()}>
      <Modal.Container size="md" scroll="inside">
        <Modal.Dialog className="app-task-creator-dialog">
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>{t('cmod.task.heading')}</Modal.Heading>
          </Modal.Header>
          <Modal.Body>
            <div className="flex flex-col gap-5">
              <TextField fullWidth value={title} onChange={setTitle}>
                <Label>{t('cmod.task.titleLabel')}</Label>
                <Input variant="secondary" maxLength={LIMITS.task.titleMax} placeholder={t('cmod.task.titlePlaceholder')} autoFocus />
              </TextField>

              <Field label={t('cmod.task.itemsLabel', { count: validItems.length })}>
                <div className="space-y-1.5">
                  {items.map((value, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <TextField
                        className="min-w-0 flex-1"
                        aria-label={t('cmod.task.itemAria', { n: idx + 1 })}
                        value={value}
                        onChange={(v) => updateItem(idx, v)}
                      >
                        <Input variant="secondary" maxLength={LIMITS.task.labelMax} placeholder={t('cmod.task.itemAria', { n: idx + 1 })} />
                      </TextField>
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRow(idx)}
                          title={t('cmod.task.removeItem')}
                          aria-label={t('cmod.task.removeItem')}
                          className="grid size-11 shrink-0 place-items-center rounded-lg text-ink-muted transition-colors hover:bg-danger/15 hover:text-danger-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        >
                          <CloseIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {items.length < 30 && (
                    <button
                      type="button"
                      onClick={addRow}
                      className="flex min-h-11 w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-ink-dim transition-colors hover:bg-default/50 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      <PlusIcon className="h-3.5 w-3.5" />
                      {t('cmod.task.addItem')}
                    </button>
                  )}
                </div>
              </Field>

              <div className="space-y-1 pt-1">
                <ToggleRow
                  label={t('cmod.task.allowCompleteLabel')}
                  description={t('cmod.task.allowCompleteDesc')}
                  checked={allowComplete}
                  onToggle={() => setAllowComplete((v) => !v)}
                />
                <ToggleRow
                  label={t('cmod.task.allowAddLabel')}
                  description={t('cmod.task.allowAddDesc')}
                  checked={allowAdd}
                  onToggle={() => setAllowAdd((v) => !v)}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onClick={() => !submitting && props.onClose()}>
              {t('cmod.cancel')}
            </Button>
            <Button variant="primary" onClick={() => void submit()} disabled={!canSubmit} loading={submitting}>
              {t('cmod.task.create')}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function ToggleRow(props: {
  label: string;
  description?: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={props.checked}
      onClick={props.onToggle}
      className="flex min-h-12 w-full items-center gap-3 rounded-lg px-1 py-2.5 text-left transition-colors hover:bg-default/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-normal text-ink">{props.label}</div>
        {props.description && <div className="text-xs leading-snug text-ink-muted">{props.description}</div>}
      </div>
      <span
        aria-hidden
        className={
          'grid h-5 w-9 shrink-0 place-items-center rounded-full transition-colors ' +
          (props.checked ? 'bg-accent' : 'bg-default')
        }
      >
        <span
          className={
            'block h-4 w-4 rounded-full bg-bg shadow-soft transition-transform ' +
            (props.checked ? 'translate-x-2' : '-translate-x-2')
          }
        />
      </span>
    </button>
  );
}
